import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Star } from "lucide-react";
import { api, formatApiError, money, compact } from "@/lib/api";
import { PageHead } from "@/components/admin/PageHead";
import ListingFormDialog from "@/components/admin/ListingFormDialog";
import ConfirmDelete from "@/components/admin/ConfirmDelete";
import { FALLBACK_IMG } from "@/components/site/PropertyCard";

const FIELDS = [
  { key: "title", label: "Property title" },
  { key: "location", label: "Location" },
  { key: "property_type", label: "Type (e.g. Residential)" },
  { key: "valuation", label: "Valuation (USD)", type: "number" },
  { key: "share_price", label: "Price per share (USD)", type: "number" },
  { key: "total_shares", label: "Total shares", type: "number", step: "1" },
  { key: "rental_yield", label: "Rental yield (%)", type: "number" },
  { key: "image_url", label: "Image URL", required: false },
];
const EMPTY = { title: "", location: "", property_type: "Residential", valuation: "", share_price: "", total_shares: "", rental_yield: "", image_url: "", description: "", featured: false };

export default function AdminProperties() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const load = useCallback(() => api.get("/properties").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const remove = async (p) => {
    try { await api.delete(`/admin/properties/${p.id}`); toast.success(`${p.title} deleted`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-properties-page">
      <PageHead title="Properties" subtitle="Manage real estate listings, share prices and yields.">
        <button onClick={() => setEditing(null)} className="btn-gold" data-testid="admin-add-property-button"><Plus className="h-4 w-4" /> Add property</button>
      </PageHead>
      <div className="panel overflow-x-auto">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead><tr className="border-b border-line text-slate-500">
            <th className="px-6 py-4 font-medium">Property</th><th className="px-6 py-4 text-right font-medium">Valuation</th>
            <th className="px-6 py-4 text-right font-medium">Per share</th><th className="px-6 py-4 text-right font-medium">Yield</th>
            <th className="px-6 py-4 text-right font-medium">Available</th><th className="px-6 py-4" />
          </tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-line/60 last:border-0" data-testid={`admin-property-row-${p.id}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-4">
                    <img src={p.image_url || FALLBACK_IMG} alt="" className="h-12 w-16 rounded-lg object-cover" />
                    <div>
                      <p className="flex items-center gap-2 font-medium text-white">{p.title}{p.featured && <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />}</p>
                      <p className="text-xs text-slate-500">{p.location} · {p.property_type}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-right font-mono">{compact(p.valuation)}</td>
                <td className="px-6 py-4 text-right font-mono">{money(p.share_price, 0)}</td>
                <td className="px-6 py-4 text-right font-mono text-amber-500">{p.rental_yield}%</td>
                <td className="px-6 py-4 text-right font-mono text-slate-400">{p.available_shares}/{p.total_shares}</td>
                <td className="px-6 py-4 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(p)} className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-white/5 hover:text-white" data-testid={`admin-edit-property-${p.id}`}><Pencil className="h-4 w-4" /></button>
                  <ConfirmDelete label={p.title} onConfirm={() => remove(p)} testId={`admin-delete-property-${p.id}`} />
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500">No properties yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <ListingFormDialog open={editing !== undefined} onOpenChange={(o) => !o && setEditing(undefined)} item={editing}
        empty={EMPTY} fields={FIELDS} endpoint="properties" assetType="property" title="Property" onSaved={load} />
    </div>
  );
}
